import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MemoInputs, MemoUsage, streamMemo } from './generateMemo';
import { downloadMemoPdf } from './memoPdf';
import styles from './MemoBuilder.module.css';

// Claude Haiku 5.5 list prices (USD per million tokens, prompts up to 100K tokens).
const INPUT_PRICE_PER_MTOK = 0.1;
const OUTPUT_PRICE_PER_MTOK = 0.5;

type Status = 'idle' | 'generating' | 'done' | 'stopped' | 'error';

interface Props {
  inputs: MemoInputs;
  ready: boolean;
  notReadyHint: string;
}

function formatTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function formatCost(usage: MemoUsage): string {
  const cost =
    (usage.input_tokens * INPUT_PRICE_PER_MTOK + usage.output_tokens * OUTPUT_PRICE_PER_MTOK) / 1_000_000;
  return cost < 0.01 ? '< $0.01' : `$${cost.toFixed(2)}`;
}

const MemoOutput: React.FC<Props> = ({ inputs, ready, notReadyHint }) => {
  const [status, setStatus] = useState<Status>('idle');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState('');
  const [usage, setUsage] = useState<MemoUsage | null>(null);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Follow the memo as it streams in, unless the reader has scrolled up.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && status === 'generating' && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [memo, status]);

  const generate = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus('generating');
    setMemo('');
    setError('');
    setUsage(null);
    stickToBottom.current = true;

    let failed = false;
    try {
      await streamMemo(
        inputs,
        (event) => {
          if (event.type === 'text') setMemo((m) => m + event.text);
          else if (event.type === 'done') setUsage(event.usage);
          else {
            failed = true;
            setError(event.error);
          }
        },
        controller.signal,
      );
      setStatus(failed ? 'error' : 'done');
    } catch (err) {
      if (controller.signal.aborted) {
        setStatus('stopped');
        return;
      }
      setError(
        err instanceof TypeError
          ? 'Could not reach the memo service. Check your connection and try again.'
          : err instanceof Error
            ? err.message
            : 'Something went wrong.',
      );
      setStatus('error');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(memo);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked; Download still works.
    }
  };

  const download = async () => {
    setExporting(true);
    try {
      await downloadMemoPdf(memo, inputs.deal.companyName);
    } catch {
      setError('Could not create the PDF. Try again, or use Copy.');
    } finally {
      setExporting(false);
    }
  };

  const generating = status === 'generating';

  return (
    <div className={styles.memoPanel}>
      <div className={styles.memoActions}>
        {generating ? (
          // Distinct keys so React swaps elements instead of morphing one button's
          // styles into the other's under the cursor.
          <button key="stop" type="button" className={styles.secondaryButton} onClick={() => abortRef.current?.abort()}>
            Stop
          </button>
        ) : (
          <button key="generate" type="button" className={styles.primaryButton} onClick={generate} disabled={!ready}>
            {memo ? 'Regenerate memo' : 'Generate memo'}
          </button>
        )}
        {memo && !generating && (
          <>
            <button type="button" className={styles.linkButton} onClick={copy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button type="button" className={styles.linkButton} onClick={download} disabled={exporting}>
              {exporting ? 'Preparing PDF...' : 'Download PDF'}
            </button>
          </>
        )}
      </div>
      {!ready && !generating && <p className={styles.muted}>{notReadyHint}</p>}

      {error && <p className={styles.error}>{error}</p>}

      {memo || generating ? (
        <div
          ref={scrollRef}
          className={styles.memoScroll}
          onScroll={(e) => {
            const el = e.currentTarget;
            stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          }}
        >
          {memo ? (
            <article className={`${styles.memo} ${generating ? styles.memoStreaming : ''}`}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{memo}</ReactMarkdown>
            </article>
          ) : (
            <div className={styles.memoWaiting}>
              <span className={styles.spinner} aria-hidden />
              Reading the deck and notes...
            </div>
          )}
        </div>
      ) : (
        <div className={styles.outputPlaceholder}>
          The memo will be written here, citing slides and calls as sources.
        </div>
      )}

      {(usage || status === 'stopped') && (
        <p className={styles.memoFooter}>
          {status === 'stopped'
            ? 'Stopped before finishing.'
            : usage &&
              `Claude Haiku 5.5 · ${formatTokens(usage.input_tokens)} in / ${formatTokens(
                usage.output_tokens,
              )} out · est. ${formatCost(usage)}`}
        </p>
      )}
    </div>
  );
};

export default MemoOutput;
